import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { BrowserRouter } from 'react-router-dom'
import { Toaster } from 'sonner'
import App from './App'
import { reloadOnceForStaleChunk, rearmStaleChunkReload } from '@/lib/staleChunkReload'
import './index.css'

// Show error in DOM. Build nodes with textContent so error text (which can
// include attacker-influenced URLs/messages) is never parsed as HTML.
function showError(title: string, message: string) {
  const root = document.getElementById('root')
  if (!root) return
  const container = document.createElement('div')
  container.style.cssText = 'padding: 20px; color: red; font-family: monospace;'
  const heading = document.createElement('h1')
  heading.textContent = title
  const body = document.createElement('pre')
  body.style.cssText = 'white-space: pre-wrap; word-break: break-word;'
  body.textContent = message
  container.append(heading, body)
  root.replaceChildren(container)
}

// Global error handler for synchronous errors
window.onerror = (message, source, lineno, colno, error) => {
  if (reloadOnceForStaleChunk(error ?? message)) return true
  // Benign browser notice (a resize callback deferred layout by one frame — common
  // with pickers/ReactFlow); not an application error, and showError would unmount
  // the whole app over it.
  if (typeof message === 'string' && message.includes('ResizeObserver loop')) return true
  console.error('Global error:', { message, source, lineno, colno, error })
  showError('JavaScript Error', `${message}\n\nSource: ${source}\nLine: ${lineno}, Col: ${colno}\n\n${error?.stack || ''}`)
  return true
}

// Global handler for unhandled promise rejections
window.onunhandledrejection = (event) => {
  console.error('Unhandled promise rejection:', event.reason)
  showError('Unhandled Promise Rejection', `${event.reason}\n\n${event.reason?.stack || ''}`)
}

// A tab opened before a deploy asks for the previous build's chunks. Reload
// once to pick up the new build. The ErrorBoundary calls the same helper for
// the failures that arrive through React.lazy instead of the preloader.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForStaleChunk((event as { payload?: unknown }).payload)) {
    event.preventDefault()
  }
})

rearmStaleChunkReload()

const rootElement = document.getElementById('root')
if (rootElement) {
  try {
    createRoot(rootElement).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
            <Toaster position="top-right" richColors closeButton />
          </BrowserRouter>
        </QueryClientProvider>
      </StrictMode>
    )
  } catch (error) {
    console.error('Render error:', error)
    showError('Render Error', error instanceof Error ? (error.stack ?? error.message) : String(error))
  }
}
